import { useSendSms } from '@/hooks/useSms';
import { useTranslation } from '@kgc/i18n';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Label,
  Textarea,
} from '@kgc/ui';
import { MessageSquare } from 'lucide-react';
import { useState } from 'react';
import { smsOutcomeStatus, type SmsReminderStatus } from './sms-reminder-status';

/**
 * SMS-emlékeztető modal — kézi lejárt-bérlés értesítés (39-kör FE-3, Epic 67 pultos-flow).
 * A gate-elt `POST /sms/send`-en át küld (a `useSendSms` reuse); a valós küldés a
 * `SMS_OUTBOUND_MODE` BE-gate-től függ (dev4-en jelenleg UNSET → BLOCK). A FE SOHA nem
 * hazudik »elküldve«-t: az őszinte-státusz (`smsOutcomeStatus`) a #731 pinnelt wire szerint.
 */

const STATUS_CLASS: Record<SmsReminderStatus, string> = {
  sent: 'text-green-600 dark:text-green-400',
  blocked: 'text-amber-600 dark:text-amber-400',
  dryRun: 'text-amber-600 dark:text-amber-400',
  failed: 'text-red-600 dark:text-red-400',
};

interface SmsReminderModalProps {
  phone: string;
  customerName: string;
  equipmentName: string;
  daysOverdue: number;
  /** Egyedi testId a trigger-gombra (desktop vs mobil sor). */
  testId: string;
}

export function SmsReminderModal({
  phone,
  customerName,
  equipmentName,
  daysOverdue,
  testId,
}: SmsReminderModalProps) {
  const { t } = useTranslation();
  const sendSms = useSendSms();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<SmsReminderStatus | null>(null);

  const defaultMessage = t('rental.smsReminder.defaultMessage', {
    customerName,
    equipmentName,
    days: String(daysOverdue),
  });

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (next) {
      // Nyitáskor friss default-üzenet + státusz-reset (a modal újrahasználható soronként).
      setMessage(defaultMessage);
      setStatus(null);
      sendSms.reset();
    }
  }

  async function handleSend() {
    setStatus(null);
    try {
      const res = await sendSms.mutateAsync({ phoneNumber: phone, message });
      setStatus(smsOutcomeStatus(res.data));
    } catch {
      // Hálózati / 5xx hiba — őszintén sikertelen (nem »elküldve«).
      setStatus('failed');
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          aria-label={t('rental.smsReminder.triggerLabel')}
          onClick={e => e.stopPropagation()}
          className="mt-0.5 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
        >
          <MessageSquare className="h-3 w-3" aria-hidden="true" />
          {t('rental.smsReminder.triggerLabel')}
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" onClick={e => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>{t('rental.smsReminder.title')}</DialogTitle>
          <DialogDescription>
            {t('rental.smsReminder.recipientLabel')}: <span className="font-medium">{phone}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          <Label htmlFor="sms-reminder-message">{t('rental.smsReminder.messageLabel')}</Label>
          <Textarea
            id="sms-reminder-message"
            data-testid="sms-reminder-message"
            rows={5}
            value={message}
            onChange={e => setMessage(e.target.value)}
          />
          {status !== null && (
            <p
              data-testid="sms-reminder-status"
              className={`text-sm font-medium ${STATUS_CLASS[status]}`}
            >
              {t(`rental.smsReminder.status.${status}`)}
            </p>
          )}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              {t('rental.smsReminder.close')}
            </Button>
          </DialogClose>
          <Button
            type="button"
            data-testid="sms-reminder-send"
            disabled={sendSms.isPending || message.trim().length === 0}
            onClick={handleSend}
          >
            {sendSms.isPending ? t('rental.smsReminder.sending') : t('rental.smsReminder.send')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
